import React, { useContext, useState, useCallback } from 'react';
import { Display1 } from 'venn-ui-kit';
import type { PortfolioCompare } from 'venn-api';
import { useHasFF } from 'venn-utils';
import { AnalysisCover, Grid } from '../Layout';
import type { SidePanelProps } from '../side-panel/SidePanel';
import SidePanel from '../side-panel/SidePanel';
import { getQueryStringParam } from '../../utils';
import OptimalPortfolioContextStore from '../OptimizationContextStore';
import ComparisonContextStore from '../ComparisonContextStore';
import AnalysisContent, { type AnalysisContentProps } from '../AnalysisContent';
import BenchmarksContextStore from '../BenchmarksContextStore';
import TagsContextStore from '../TagsContextStore';
import EmptyWorkspace from './EmptyWorkspace';
import type { History } from 'history';
import PortfolioManageDataAnalysisStore from '../PortfolioManageDataAnalysisStore';
import {
  UserContext,
  ResizableAllocationPanelWrapper,
  ALLOCATOR_TOTAL_WIDTH_WITH_HISTORICALS,
  ALLOCATOR_TOTAL_WIDTH,
} from 'venn-components';

export interface AnalysisProps extends AnalysisContentProps, SidePanelProps {
  history: History;
  onBenchmarksUpdate: (compare: PortfolioCompare[]) => Promise<void>;
}

const Analysis = ({
  history,
  /** Template */
  templateId,
  templates,
  onChangeAnalysisTemplate,
  onEditTemplate,
  /** Subject */
  objectId,
  objectType,
  loadingSubject,
  onChangeSubject,
  analysisConfig,
  /** Analyses */
  analysisLoading,
  analyses,
  analysesError,
  updateAnalysisStatusForTracking,
  /** Portfolio */
  hasAllocationError,
  onUpdateAllocationError,
  allUpdatedFunds,
  onStrategyChange,
  onPortfolioChange,
  onComparisonChange,
  onPortfolioCreate,
  /** Funds */
  updateFund,
  onFundUpdated,
  updateAllFunds,
  /** Parameters */
  onToggleRelative,
  canToggleRelative,
  setTimeFrame,
  onResetTimeFrame,
  defaultAnalysesPeriod,
  setCategoryConfig,
  factorLenses,
  onForecastUpdate,
  onBenchmarksUpdate,
  currentViewName,
  actualTimeFrame,
}: AnalysisProps) => {
  const { hasPermission } = useContext(UserContext);
  const hasAccessToCompare = hasPermission('COMPARE');
  const canUploadInvestments = hasPermission('UPLOAD_RETURNS');
  const hasHistoricals = useHasFF('historical_portfolios_ff');

  const defaultPanelWidth = hasHistoricals ? ALLOCATOR_TOTAL_WIDTH_WITH_HISTORICALS : ALLOCATOR_TOTAL_WIDTH;
  const [panelWidth, setPanelWidth] = useState<number>(defaultPanelWidth);

  const handleWidthChange = useCallback((width: number) => {
    setPanelWidth(width);
  }, []);

  if (!objectId) {
    return <EmptyWorkspace onChangeSubject={onChangeSubject} />;
  }

  const gridPanelWidth = objectType === 'investment' ? 0 : panelWidth;

  return (
    <Grid panelWidth={gridPanelWidth}>
      <OptimalPortfolioContextStore portfolio={analysisConfig.subject?.portfolio} onForecastUpdate={onForecastUpdate}>
        <ComparisonContextStore
          analysisSubject={analysisConfig.subject}
          optimizationId={getQueryStringParam(history.location, 'optimizationId') as string}
        >
          <BenchmarksContextStore subject={analysisConfig.subject} onBenchmarksUpdate={onBenchmarksUpdate}>
            <TagsContextStore>
              <PortfolioManageDataAnalysisStore
                config={analysisConfig}
                setCategoryConfig={setCategoryConfig}
                onFundUpdated={onFundUpdated}
              >
                {objectType === 'portfolio' && (
                  <ResizableAllocationPanelWrapper
                    defaultWidth={defaultPanelWidth}
                    minWidth={defaultPanelWidth}
                    onWidthChange={handleWidthChange}
                  >
                    <SidePanel
                      objectType={objectType}
                      objectId={objectId}
                      subject={analysisConfig.subject}
                      loadingSubject={loadingSubject}
                      allUpdatedFunds={allUpdatedFunds}
                      onStrategyChange={onStrategyChange}
                      onPortfolioChange={onPortfolioChange}
                      onComparisonChange={onComparisonChange}
                      onPortfolioCreate={onPortfolioCreate}
                      hasAllocationError={hasAllocationError}
                      onUpdateAllocationError={onUpdateAllocationError}
                      updateFund={updateFund}
                      updateAllFunds={updateAllFunds}
                      hasAccessToCompare={hasAccessToCompare}
                      canUploadInvestments={canUploadInvestments}
                    />
                  </ResizableAllocationPanelWrapper>
                )}
                {templateId && (
                  <AnalysisContent
                    defaultAnalysesPeriod={defaultAnalysesPeriod}
                    onToggleRelative={onToggleRelative}
                    canToggleRelative={!!canToggleRelative}
                    setTimeFrame={setTimeFrame}
                    setCategoryConfig={setCategoryConfig}
                    templateId={templateId}
                    analysisConfig={analysisConfig}
                    analysesError={analysesError}
                    analysisLoading={analysisLoading}
                    factorLenses={factorLenses}
                    onResetTimeFrame={onResetTimeFrame}
                    analyses={analyses}
                    onForecastUpdate={onForecastUpdate}
                    templates={templates}
                    onChangeAnalysisTemplate={onChangeAnalysisTemplate}
                    onEditTemplate={onEditTemplate}
                    onFundUpdated={onFundUpdated}
                    onChangeSubject={onChangeSubject}
                    updateAnalysisStatusForTracking={updateAnalysisStatusForTracking}
                    currentViewName={currentViewName}
                    actualTimeFrame={actualTimeFrame}
                  />
                )}
              </PortfolioManageDataAnalysisStore>
            </TagsContextStore>
          </BenchmarksContextStore>
        </ComparisonContextStore>
        {hasAllocationError && (
          <AnalysisCover>
            <Display1>To resume analysis, ensure investment allocations sum up to 100%</Display1>
          </AnalysisCover>
        )}
      </OptimalPortfolioContextStore>
    </Grid>
  );
};

export default Analysis;
